// frontend/src/hooks/useBackgroundRunStatus.js
//
// Watch a merchant-started background run: poll its status endpoint while
// the backend reports it in progress (backend: core/scheduling/queue.py
// pipeline_run_state), and announce what the finished run changed
// (`queries/dataChanged`). How the answer READS is utils/backgroundRun.js.

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiJson } from '../queries/api';
import { useDataChanged } from '../queries/dataChanged';

// One indexed row per poll; a run takes seconds to minutes.
const POLL_MS = 3000;

/**
 * @param {object} args
 * @param {Array} args.queryKey     the status query's key
 * @param {string} args.url         the status endpoint
 * @param {string[]} args.changesOnFinish  the kinds of data (`DATA`) a finished run changed
 * @param {boolean} [args.enabled]  hold the poll until the caller has a run
 * @returns {{state: object|null, error: Error|null}}
 */
export function useBackgroundRunStatus({ queryKey, url, changesOnFinish, enabled = true }) {
  const changed = useDataChanged();

  const query = useQuery({
    queryKey,
    queryFn: () => apiJson.get(url),
    enabled,
    staleTime: 0,
    refetchInterval: ({ state }) => (state.data?.in_progress ? POLL_MS : false),
  });

  // Keyed on the run id, so a second run announces again while a re-render
  // (or a poll tick after the run settled) does not — the effect re-runs on
  // every render whose kinds array is new, and returns early.
  const announcedRunRef = useRef(null);
  const finishedRunId = query.data && !query.data.in_progress
    ? query.data.run_id : null;

  useEffect(() => {
    if (finishedRunId === null) return;
    if (announcedRunRef.current === finishedRunId) return;
    announcedRunRef.current = finishedRunId;
    changed(changesOnFinish);
  }, [finishedRunId, changesOnFinish, changed]);

  return {
    state: query.data ?? null,
    error: query.isError ? query.error : null,
  };
}
